/**
 * ConfigIAActas — IMPL-047 Sprint C+D (2026-05-22).
 *
 * Panel de configuración de la IA que procesa actas (modelos, prompts, contexto,
 * notificaciones, Gmail). Renderizado desde AdminPanel.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, Mail, Save, FlaskConical, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const MODELOS = [
  { v: "gemini", label: "Gemini 3 Flash" },
  { v: "claude", label: "Claude Sonnet 4.5" },
];

export default function ConfigIAActas() {
  const [cfg, setCfg] = useState(null);
  const [comerciales, setComerciales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTexto, setTestTexto] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, u] = await Promise.all([
          authAxios.get(`${API}/config-actividad-ia`),
          authAxios.get(`${API}/users`),
        ]);
        setCfg(c.data);
        setComerciales(u.data || []);
      } catch {
        toast.error("Error cargando configuración");
      }
      setLoading(false);
    })();
  }, []);

  const guardar = async () => {
    setSaving(true);
    try {
      await authAxios.put(`${API}/config-actividad-ia`, cfg);
      toast.success("Configuración guardada");
    } catch {
      toast.error("Error guardando");
    }
    setSaving(false);
  };

  const probar = async () => {
    if (!testTexto.trim()) {
      toast.error("Escribe un texto de prueba");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await authAxios.post(`${API}/config-actividad-ia/test-ia`, { texto: testTexto });
      setTestResult(r.data);
      toast.success("Test IA OK");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error ejecutando test IA");
    }
    setTesting(false);
  };

  const gmailTest = async () => {
    try {
      const r = await authAxios.post(`${API}/actividad/gmail-test`);
      const d = r.data;
      toast.success(`Gmail · procesados ${d.procesados} · actas ${d.actas_creadas} · ${d.status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error ejecutando Gmail test");
    }
  };

  if (loading || !cfg) return <div className="text-center py-12 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6">
      <Section title="Modelos IA por función" icon={<Sparkles className="w-4 h-4" />}>
        {[
          { k: "modelo_resumen", label: "Resumen + título + acuerdos" },
          { k: "modelo_valoracion", label: "Valoración comercial (semáforo)" },
          { k: "modelo_tareas", label: "Extracción de tareas" },
        ].map(({ k, label }) => (
          <div key={k} className="flex items-center justify-between gap-3">
            <span className="text-sm">{label}</span>
            <select
              value={cfg[k] || "gemini"}
              onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
              data-testid={`cfg-${k}`}
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
            >
              {MODELOS.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
            </select>
          </div>
        ))}
      </Section>

      <Section title="Contexto MOBILSAFE (se inyecta en todos los prompts)">
        <textarea
          value={cfg.contexto_mobilsafe || ""}
          onChange={(e) => setCfg({ ...cfg, contexto_mobilsafe: e.target.value })}
          data-testid="cfg-contexto"
          rows={4}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-y"
          placeholder="Descripción de MOBILSAFE, productos, propuesta de valor..."
        />
      </Section>

      <Section title="Prompts (avanzado)">
        {[
          { k: "prompt_resumen", label: "Prompt resumen" },
          { k: "prompt_valoracion", label: "Prompt valoración" },
          { k: "prompt_tareas", label: "Prompt tareas" },
        ].map(({ k, label }) => (
          <div key={k}>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold block mb-1">{label}</label>
            <textarea
              value={cfg[k] || ""}
              onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
              data-testid={`cfg-${k}`}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-y"
            />
          </div>
        ))}
      </Section>

      <Section title="Notificaciones por email" icon={<Mail className="w-4 h-4" />}>
        <p className="text-xs text-slate-500 mb-2">Recibirán un email cada vez que se cree (o se actualice con IA) una nueva acta.</p>
        <div className="space-y-1">
          {comerciales.map(u => {
            const activo = (cfg.notificaciones_actas || []).includes(u.id);
            return (
              <label key={u.id} className="flex items-center gap-2 text-sm py-1">
                <input
                  type="checkbox"
                  checked={activo}
                  onChange={(e) => {
                    const lista = cfg.notificaciones_actas || [];
                    setCfg({
                      ...cfg,
                      notificaciones_actas: e.target.checked
                        ? [...lista, u.id]
                        : lista.filter(x => x !== u.id)
                    });
                  }}
                  data-testid={`cfg-notif-${u.id}`}
                  className="w-4 h-4 accent-emerald-600"
                />
                <span className="text-slate-700">{u.nombre || u.email}</span>
                <span className="text-xs text-slate-400">{u.email}</span>
              </label>
            );
          })}
        </div>
      </Section>

      <Section title="Gmail · sync automático (Sprint D)">
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Requiere variables de entorno <code>GMAIL_CREDENTIALS_JSON</code> y <code>GMAIL_TOKEN_JSON</code> en <code>/app/backend/.env</code>. El cron se ejecuta cada 2h; solo procesa si está activo y las credenciales están presentes.</span>
        </p>
        <label className="flex items-center gap-2 text-sm py-1">
          <input
            type="checkbox"
            checked={!!cfg.gmail_activo}
            onChange={(e) => setCfg({ ...cfg, gmail_activo: e.target.checked })}
            data-testid="cfg-gmail-activo"
            className="w-4 h-4 accent-emerald-600"
          />
          Activar sincronización Gmail
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold block mb-1">Frecuencia (horas)</label>
            <input
              type="number"
              min={1}
              max={24}
              value={cfg.gmail_frecuencia_horas || 2}
              onChange={(e) => setCfg({ ...cfg, gmail_frecuencia_horas: parseInt(e.target.value || 2) })}
              data-testid="cfg-gmail-frecuencia"
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm w-24"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold block mb-1">Label filtro</label>
            <input
              type="text"
              value={cfg.gmail_label_filtro || "INBOX"}
              onChange={(e) => setCfg({ ...cfg, gmail_label_filtro: e.target.value })}
              data-testid="cfg-gmail-label"
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm w-full"
            />
          </div>
        </div>
        <button
          onClick={gmailTest}
          data-testid="cfg-gmail-test"
          className="text-xs px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded flex items-center gap-1"
        >
          <FlaskConical className="w-3.5 h-3.5" /> Ejecutar cron Gmail ahora
        </button>
      </Section>

      <Section title="Test IA" icon={<FlaskConical className="w-4 h-4" />}>
        <p className="text-xs text-slate-500">Pega un texto de prueba y observa cómo lo procesarían los 3 prompts en vivo.</p>
        <textarea
          value={testTexto}
          onChange={(e) => setTestTexto(e.target.value)}
          data-testid="cfg-test-texto"
          rows={3}
          placeholder="Ej: Visita a Transportes Pérez el 22 may. Tienen 80 furgonetas. Interés en CAES. Acuerdos: enviar propuesta antes del 30 may, llamar a Maite..."
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-y"
        />
        <button
          onClick={probar}
          disabled={testing || !testTexto.trim()}
          data-testid="cfg-test-btn"
          className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg flex items-center gap-1.5"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
          {testing ? "Ejecutando…" : "Probar IA"}
        </button>

        {testResult && (
          <div className="space-y-3 mt-2" data-testid="cfg-test-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1">Resumen</p>
              <p className="text-sm font-semibold text-slate-800">{testResult.resumen?.titulo}</p>
              <p className="text-xs text-slate-700 mt-1">{testResult.resumen?.resumen}</p>
              {(testResult.resumen?.acuerdos || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {testResult.resumen.acuerdos.map((a, i) => <span key={i} className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">{a}</span>)}
                </div>
              )}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-[10px] uppercase tracking-wider text-blue-700 font-bold mb-1">Valoración</p>
              <p className="text-sm text-slate-800">Semáforo: <strong>{testResult.valoracion?.semaforo}</strong> · Probabilidad: <strong>{testResult.valoracion?.probabilidad_cierre}%</strong></p>
              {testResult.valoracion?.recomendacion && <p className="text-xs text-slate-700 mt-1 italic">{testResult.valoracion.recomendacion}</p>}
            </div>
            <div className="bg-violet-50 border border-violet-200 rounded p-3">
              <p className="text-[10px] uppercase tracking-wider text-violet-700 font-bold mb-1">Tareas ({(testResult.tareas || []).length})</p>
              <ul className="text-xs text-slate-700 space-y-0.5">
                {(testResult.tareas || []).map((t, i) => <li key={i}>· {t.texto} <span className="text-slate-400">({t.urgencia})</span></li>)}
              </ul>
            </div>
          </div>
        )}
      </Section>

      <div className="flex justify-end gap-2 sticky bottom-0 bg-white py-3 border-t border-slate-200">
        <button
          onClick={guardar}
          disabled={saving}
          data-testid="cfg-save-btn"
          className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg disabled:opacity-50 flex items-center gap-1.5"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Guardando…" : "Guardar configuración"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
        {icon} {title}
      </h3>
      {children}
    </div>
  );
}
